import { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import Modal from "../../UI/Modal";
import Select2 from "../../UI/Select2";
import Loader from "../../UI/Loader";
import Alert from "../../UI/Alert";
import { getTalentosSelectoresPlantelService } from "../../../services/planteles/talentosPlantel.service";
import {
  getConversacionesSelectoresPlantelService,
  getConfiguracionPorTalentoPlantelService,
  crearConfiguracionPorTalentoPlantelService,
} from "../../../services/plantels/contenidoPlantel.service";
import CONVERSACIONES_META from "../../conversacionesGlobales/constants";
import ContenidoPlantelFormModal from "./ContenidoPlantelFormModal";

/* ------------------------------------------------------------------ */
/*  Contenido de conversaciones por Talento (nivel plantel)            */
/* ------------------------------------------------------------------ */

const ContenidoPlantelLayer = ({ plantelId }) => {
  // Conversaciones del plantel (selectores)
  const [conversaciones, setConversaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);

  // Filtros
  const [talentoFilter, setTalentoFilter] = useState("");
  const [configData, setConfigData] = useState(null);

  // Modal formulario
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [formSaving, setFormSaving] = useState(false);

  /* ----- Cargar conversaciones del plantel ----- */
  useEffect(() => {
    const cargar = async () => {
      if (!plantelId) return;
      setLoading(true);
      setApiError(null);
      try {
        const data = await getConversacionesSelectoresPlantelService(plantelId);
        setConversaciones(Array.isArray(data) ? data : []);
      } catch (err) {
        const msg =
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message ||
          "Error al cargar conversaciones.";
        setApiError(msg);
        toast.error("No se pudieron cargar las conversaciones del plantel.");
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [plantelId]);

  /* ----- Filtros ----- */
  const fetchTalentos = async (searchTerm) => {
    const response = await getTalentosSelectoresPlantelService(plantelId, searchTerm);
    return response.map((item) => ({
      id: item.id,
      label: item.label,
    }));
  };

  const handleTalentoChange = async (value) => {
    const talentoId = value || "";
    setTalentoFilter(talentoId);
    if (!talentoId) {
      setConfigData(null);
      return;
    }
    try {
      const data = await getConfiguracionPorTalentoPlantelService(plantelId, talentoId);
      setConfigData(data);
    } catch {
      setConfigData(null);
    }
  };

  const handleClearFilters = () => {
    setTalentoFilter("");
    setConfigData(null);
  };

  const hasFilters = talentoFilter;

  /* ----- Abrir modal formulario ----- */
  const abrirFormModal = () => {
    if (!talentoFilter) {
      toast.warning("Selecciona un talento primero.");
      return;
    }
    const initial = {};
    conversaciones.forEach((conv) => {
      const existente = Array.isArray(configData?.conversaciones)
        ? configData.conversaciones.find(
            (c) => c.conversacion_plantel === conv.id
          )
        : null;
      const frases = existente?.frases || [];
      const proposito = frases.find((f) => f.tipo === "PROPOSITO");
      const utilizar = frases.find((f) => f.tipo === "UTILIZAR");
      initial[conv.id] = {
        descripcion_proposito: proposito?.frase || "",
        descripcion_utilizar: utilizar?.frase || "",
      };
    });
    setFormData(initial);
    setFormModalOpen(true);
  };

  const cerrarFormModal = () => {
    setFormModalOpen(false);
  };

  const handleFormFieldChange = (num, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [num]: {
        ...prev[num],
        [field]: value,
      },
    }));
  };

  const guardarFormulario = async () => {
    if (!talentoFilter) {
      toast.warning("Selecciona un talento primero.");
      return;
    }
    setFormSaving(true);
    try {
      const conversacionesPayload = conversaciones.map((conv) => ({
        conversacion_plantel: conv.id,
        frases: [
          {
            frase_nombre: formData[conv.id]?.descripcion_proposito || "",
            tipo: "PROPOSITO",
          },
          {
            frase_nombre: formData[conv.id]?.descripcion_utilizar || "",
            tipo: "UTILIZAR",
          },
        ],
      }));

      const payload = {
        plantel: Number(plantelId),
        talento: Number(talentoFilter),
        conversaciones: conversacionesPayload,
      };

      await crearConfiguracionPorTalentoPlantelService(payload);
      toast.success("Contenido guardado exitosamente.");
      const fresh = await getConfiguracionPorTalentoPlantelService(plantelId, talentoFilter);
      setConfigData(fresh);
      cerrarFormModal();
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        "Error al guardar.";
      toast.error(msg);
    } finally {
      setFormSaving(false);
    }
  };

  /* ----- Helpers ----- */
  const metaDe = (num) =>
    CONVERSACIONES_META[num] || {
      foco: "",
      color: "#6c757d",
      colorClaro: "#f8f9fa",
      icono: "mdi:message-outline",
    };

  /* ------------------------------------------------------------------ */
  /*  Render                                                            */
  /* ------------------------------------------------------------------ */

  return (
    <div>
      {/* Encabezado */}
      <div className="mb-3">
        <h5 className="fw-semibold">
          Contenido de las conversaciones de Mis Talentos
        </h5>
        <p className="text-muted small mb-0">
          Configura el contenido de cada conversación para los talentos de este plantel.
        </p>
      </div>

      <div className="d-flex flex-wrap align-items-center justify-content-end gap-3 mb-3">
        <button
          className="btn btn-primary d-flex align-items-center gap-2"
          onClick={abrirFormModal}
        >
          <Icon icon="mdi:pencil-outline" width="18" />
          Editar contenido
        </button>
      </div>

      {/* Filtro por talento */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <div style={{ minWidth: "200px", maxWidth: "300px" }}>
          <Select2
            value={talentoFilter}
            onChange={handleTalentoChange}
            fetchOptions={fetchTalentos}
            placeholder="Filtrar por talento..."
            isClearable
          />
        </div>
        {hasFilters && (
          <button
            onClick={handleClearFilters}
            className="btn btn-outline-secondary d-flex align-items-center gap-2"
            style={{ height: "44px" }}
          >
            <Icon icon="mdi:filter-remove-outline" width="18" />
            <span className="d-none d-sm-inline">Limpiar filtro</span>
          </button>
        )}
      </div>

      {/* Indicador de estado */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <span
          className={`badge d-flex align-items-center gap-1 py-2 px-3 rounded-pill ${
            loading
              ? "bg-warning bg-opacity-10 text-warning"
              : apiError
              ? "bg-danger bg-opacity-10 text-danger"
              : "bg-success bg-opacity-10 text-success"
          }`}
        >
          {loading ? "Cargando..." : apiError ? "Error de conexión" : ""}
        </span>
      </div>

      {/* Error */}
      {apiError && (
        <Alert
          message={apiError}
          variant="error"
          onClose={() => setApiError(null)}
        />
      )}

      {/* Loader */}
      {loading && (
        <div className="py-5">
          <Loader center text="Cargando conversaciones..." delay={200} />
        </div>
      )}

      {/* Infográfico horizontal de 5 columnas */}
      {!loading && (
        <div className="radius-12 overflow-hidden border">
          {/* Fila 1: Encabezados */}
          <div className="row g-0">
            {conversaciones.map((conv, idx) => {
              const num = idx + 1;
              const meta = metaDe(num);
              return (
                <div
                  key={`hdr-${conv.id}`}
                  className="col"
                  style={{
                    flex: "1 1 0%",
                    minWidth: 0,
                    borderRight: idx < conversaciones.length - 1 ? "1px solid #e5e7eb" : "none",
                  }}
                >
                  <div
                    className="d-flex flex-column align-items-center text-center py-4 px-3"
                    style={{ backgroundColor: meta.colorClaro }}
                  >
                    <span
                      className="rounded-circle d-flex align-items-center justify-content-center mb-2"
                      style={{ width: 48, height: 48, backgroundColor: meta.color }}
                    >
                      <Icon icon={meta.icono} width={26} className="text-white" />
                    </span>
                    <span className="fw-bold mb-1" style={{ color: meta.color, fontSize: "12px" }}>
                      CONVERSACIÓN {num}
                    </span>
                    <span className="fw-semibold" style={{ color: meta.color, fontSize: "13px", lineHeight: 1.3 }}>
                      {conv?.label || "Sin datos"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Fila 2: Propósito */}
          <div className="px-4 py-2 text-center" style={{ backgroundColor: "#f0f4f8", borderTop: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb" }}>
            <span className="fw-bold text-uppercase" style={{ fontSize: "14px", letterSpacing: "0.5px", color: "#6b7280" }}>
              Propósito de la conversación
            </span>
          </div>
          <div className="row g-0">
            {conversaciones.map((conv, idx) => {
              const meta = metaDe(idx + 1);
              const existente = Array.isArray(configData?.conversaciones)
                ? configData.conversaciones.find((c) => c.conversacion_plantel === conv.id)
                : null;
              const proposito = existente?.frases?.find((f) => f.tipo === "PROPOSITO");
              return (
                <div
                  key={`pro-${conv.id}`}
                  className="col d-flex align-items-start gap-2 py-3 px-3"
                  style={{
                    flex: "1 1 0%",
                    minWidth: 0,
                    borderRight: idx < conversaciones.length - 1 ? "1px solid #e5e7eb" : "none",
                  }}
                >
                  <span className="flex-shrink-0 mt-1">
                    <Icon icon={meta.icono} width={18} style={{ color: meta.color }} />
                  </span>
                  <span
                    className="small"
                    style={{
                      fontSize: "13px",
                      lineHeight: 1.5,
                      color: proposito?.frase ? "#374151" : "#9ca3af",
                      fontStyle: proposito?.frase ? "normal" : "italic",
                    }}
                  >
                    {proposito?.frase ||
                      (configData ? "Sin contenido." : "Selecciona un talento y haz clic en \"Editar contenido\".")}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Fila 3: Cómo utilizará el Mentor */}
          <div className="px-4 py-2 text-center" style={{ backgroundColor: "#f0f4f8", borderTop: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb" }}>
            <span className="fw-bold text-uppercase" style={{ fontSize: "14px", letterSpacing: "0.5px", color: "#6b7280" }}>
              ¿Cómo la utilizará el Mentor?
            </span>
          </div>
          <div className="row g-0">
            {conversaciones.map((conv, idx) => {
              const meta = metaDe(idx + 1);
              const existente = Array.isArray(configData?.conversaciones)
                ? configData.conversaciones.find((c) => c.conversacion_plantel === conv.id)
                : null;
              const utilizar = existente?.frases?.find((f) => f.tipo === "UTILIZAR");
              return (
                <div
                  key={`uti-${conv.id}`}
                  className="col d-flex align-items-start gap-2 py-3 px-3"
                  style={{
                    flex: "1 1 0%",
                    minWidth: 0,
                    borderRight: idx < conversaciones.length - 1 ? "1px solid #e5e7eb" : "none",
                  }}
                >
                  <span className="flex-shrink-0 mt-1">
                    <Icon icon={meta.icono} width={18} style={{ color: meta.color }} />
                  </span>
                  <span
                    className="small"
                    style={{
                      fontSize: "13px",
                      lineHeight: 1.5,
                      color: utilizar?.frase ? "#374151" : "#9ca3af",
                      fontStyle: utilizar?.frase ? "normal" : "italic",
                    }}
                  >
                    {utilizar?.frase ||
                      (configData ? "Sin contenido." : "Selecciona un talento y haz clic en \"Editar contenido\".")}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="d-flex align-items-center justify-content-between gap-2 px-4 py-2" style={{ backgroundColor: "#f9fafb", borderTop: "1px solid #e5e7eb" }}>
            <span style={{ fontSize: "11px", color: "#6b7280" }}>
              <Icon icon="mdi:information-outline" width="14" className="me-1" />
              Selecciona un talento para editar su contenido.
            </span>
          </div>
        </div>
      )}

      {/* Modal Formulario */}
      <Modal
        open={formModalOpen}
        onClose={cerrarFormModal}
        title="Editar contenido de conversaciones"
        size="fullscreen"
      >
        <ContenidoPlantelFormModal
          talentoFilter={talentoFilter}
          conversaciones={conversaciones}
          formData={formData}
          formSaving={formSaving}
          hasFilters={hasFilters}
          onClose={cerrarFormModal}
          onTalentoChange={handleTalentoChange}
          onClearFilters={handleClearFilters}
          onFieldChange={handleFormFieldChange}
          onGuardar={guardarFormulario}
          fetchTalentos={fetchTalentos}
          Select2={Select2}
        />
      </Modal>
    </div>
  );
};

export default ContenidoPlantelLayer;
